import { useAppDispatch } from '_store';
import { DialogType } from '_store/application/types';
import ivimsReducer from '_store/ivim/ivimsReducers';
import { ivimsSelector } from '_store/ivim/selectors';
import { getIvimTitle } from '_store/ivim/utils';
import { useLocalization } from '@fluent/react';
import { Grid } from '@material-ui/core';
import { isFuture } from 'date-fns';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';

import { useRolePermissions } from '../../../hooks/useRolePermissions';
import { images } from '../../icons/ivim/pictograms';
import CardDetails from '../Ivim/IvimList/Card/Details';
import EventLayout from './EventLayout';

interface IvimLayoutProps {
  id: string;
  onUpdateIvim?: (idExpanded: string) => void;
  onCloneIvim?: (idExpanded: string) => void;
}

const IvimLayout: FC<IvimLayoutProps> = ({ id, onUpdateIvim, onCloneIvim }) => {
  const { l10n } = useLocalization();
  const dispatch = useAppDispatch();
  const { canWriteIvim, blockedActionMode } = useRolePermissions();
  const writeBlockedMessage = l10n.getString('forms-rbac-readonly-action-denied');

  const events = useSelector(ivimsSelector);
  const event = events.find((ivim) => ivim.id === id);

  const onDeleteIvim = () => {
    dispatch(ivimsReducer.actions.deleteIVIM(id));
  };

  const onDisableIvim = () => {
    dispatch(ivimsReducer.actions.disableIVIM({ ids: [id], disable: true }));
  };

  const onEnableIvim = () => {
    dispatch(ivimsReducer.actions.disableIVIM({ ids: [id], disable: false }));
  };

  if (!event) {
    return (
      <EventLayout
        type={DialogType.IVIM}
        active={false}
        header={'Unknown event'}
        disabled={false}
        scheduled={false}
        auditResourceId={id}
      >
        Unknown event #{id} selected
      </EventLayout>
    );
  }

  const header = getIvimTitle(event, l10n);
  // TODO fix commented stuff after IVIM reimplementation
  return (
    <EventLayout
      type={DialogType.IVIM}
      disabled={event.disabled && new Date(event.expiresAt) > new Date()}
      active={isFuture(new Date(event.ends))}
      header={header}
      date={new Date(event.starts)}
      broadcastingFrequency={event.broadcastingFrequency.toString()}
      onDelete={onDeleteIvim}
      onDisable={onDisableIvim}
      onEnable={onEnableIvim}
      onUpdate={() => (onUpdateIvim ? onUpdateIvim(event.id) : null)}
      onClone={() => (onCloneIvim ? onCloneIvim(event.id) : null)}
      scheduled={isFuture(new Date(event.starts))}
      writeAllowed={canWriteIvim}
      blockedActionMode={blockedActionMode}
      blockedMessage={writeBlockedMessage}
      specialHeader={
        event.metadata.connectedDenms &&
        event.metadata.connectedDenms.length > 0 && (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <img
                src={images.speedLimit110}
                style={{
                  width: 'auto',
                  height: '1.5rem',
                  marginRight: '0.5rem',
                  verticalAlign: 'middle',
                }}
              />
              <strong>Max Speed Limit Reduced to 110 due to HeavyRain</strong>
            </Grid>
          </Grid>
        )
      }
    >
      <CardDetails ivim={event} />
    </EventLayout>
  );
};

export default React.memo(IvimLayout);
